import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch, cloudConfigured } from '../api'
import type { GameState } from '../types'
import type { ChallengeState, ChallengeAction } from './types'
export function useChallenge(language:string,onRegularReset:(state:GameState)=>void){
  const [data,setData]=useState<ChallengeState>({version:0,match:null,results:[]})
  const [loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[error,setError]=useState('')
  const lock=useRef(false),current=useRef(data),alive=useRef(true)
  const accept=useCallback((next:ChallengeState)=>{if(alive.current&&next.version>=current.current.version){current.current=next;setData(next)}},[])
  const refresh=useCallback(async()=>{
    if(!cloudConfigured){setLoading(false);return}
    try{const r=await apiFetch('/challenge');if(!r.ok)throw Error('The challenge could not be loaded. Try again.');accept(await r.json());if(alive.current)setError('')}
    catch(e){if(alive.current)setError(e instanceof Error?e.message:'Could not load challenge.')}
    finally{if(alive.current)setLoading(false)}
  },[accept])
  useEffect(()=>{alive.current=true;void refresh();const visible=()=>{if(document.visibilityState==='visible')void refresh()};window.addEventListener('focus',refresh);document.addEventListener('visibilitychange',visible);return()=>{alive.current=false;window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',visible)}},[refresh])
  const act=async(action:ChallengeAction,present?:(next:ChallengeState)=>Promise<void>)=>{
    if(lock.current||!cloudConfigured)return
    lock.current=true;setBusy(true);setError('')
    const snapshot=current.current
    const payload={...action,requestId:crypto.randomUUID(),version:snapshot.version,matchId:snapshot.match?.id,language:action.action==='start'||action.action==='restart'?language:snapshot.match?.language??language}
    try{
      const send=()=>apiFetch('/challenge',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)})
      // Retry an uncertain transport outcome with the SAME idempotency key.
      let response:Response
      try{response=await send()}catch{response=await send()}
      const body=await response.json()
      if(response.ok&&present)await present(body)
      if(Number.isInteger(body.version))accept(body)
      if(response.ok&&body.regularState)onRegularReset(body.regularState)
      if(!response.ok)throw Error(body.message??'Your move could not be saved. Retry when connected.')
      return true
    }catch(e){if(alive.current)setError(e instanceof Error?e.message:'Your move could not be saved.');void refresh()}
    finally{lock.current=false;if(alive.current)setBusy(false)}
  }
  return {data,loading,busy,error,act,refresh,available:cloudConfigured}
}
